import api from '@mint-lib/api';
import { useCrucialQuery, type RefetchOptions } from '@mint-lib/query-client';

import { queryClient } from '../api/queries/client.js';
import { MEDIA_PLANS } from '../constants/EndpointConstants.js';
import { QueryKey } from '../constants/queryKeys.js';
import {
  BuyingPlanInfoResponseSchema,
  type BuyingPlanInfo,
  type BuyingPlanInfoRequestPayload,
} from '../types/buyingPlanInfo.js';
import { localeCompare } from '../utils/helpers.js';
import { useCampaignCurrencyMode } from './useCampaignCurrencyMode.js';
import { useBuilderTableViewDataSelector } from './useGetBuilderTableViewData.js';
import { useSelectedBuyingPlanId } from './useSelectedBuyingPlanId.js';

const getBuyingPlanData = async ({
  buyingPlanId,
  includeFields,
  multiCurrency,
}: BuyingPlanInfoRequestPayload) => {
  const response = await api.get(`${MEDIA_PLANS}${buyingPlanId}/`, {
    params: {
      include_fields: JSON.stringify(includeFields),
      multi_currency: multiCurrency,
    },
  });
  return BuyingPlanInfoResponseSchema.parse(response.data);
};

const useGetBuyingPlanPayload = () => {
  const [isCampaignCurrencyMode] = useCampaignCurrencyMode();
  const selectedPlanId = useSelectedBuyingPlanId();
  const { data: columns } = useBuilderTableViewDataSelector('columns');

  const includeFields =
    columns?.map((column) => column.name).toSorted(localeCompare) ?? [];

  return {
    buyingPlanId: selectedPlanId,
    includeFields,
    multiCurrency: !isCampaignCurrencyMode,
  };
};

export const useGetBuyingPlanInfoData = (
  payload: BuyingPlanInfoRequestPayload,
  options?: { skip: boolean },
) => {
  const { isLoading, isError, data } = useCrucialQuery({
    queryKey: [
      QueryKey.BUYING_PLAN_INFO,
      {
        ...payload,
        includeFields: payload.includeFields.toSorted(localeCompare),
      },
    ],
    queryFn: () => getBuyingPlanData(payload),
    enabled: !options?.skip && !!payload.buyingPlanId,
    staleTime: Infinity,
  });

  return { data, isLoading, isError };
};

// Use useBuyingPlanInfoSelector if you need more than 1 property of BuyingPlanInfo
export const useBuyingPlanInfoSelector = () => {
  const payload = useGetBuyingPlanPayload();

  return queryClient.getQueryData<BuyingPlanInfo>([
    QueryKey.BUYING_PLAN_INFO,
    payload,
  ]);
};

export const useRefetchBuyingPlanInfo = () => {
  const payload = useGetBuyingPlanPayload();
  const { buyingPlanId, includeFields } = payload;

  const refetch = async (options: RefetchOptions = { throwOnError: true }) => {
    if (!buyingPlanId || !includeFields) {
      return;
    }

    await queryClient.refetchQueries(
      {
        queryKey: [QueryKey.BUYING_PLAN_INFO, payload],
      },
      options,
    );
  };
  return refetch;
};

export const useUpdateBuyingPlanInfoCache = () => {
  const payload = useGetBuyingPlanPayload();
  const { buyingPlanId, includeFields } = payload;

  const updateCache = (newData: Partial<BuyingPlanInfo>) => {
    if (!buyingPlanId || !includeFields) {
      return;
    }

    queryClient.setQueryData<BuyingPlanInfo>(
      [QueryKey.BUYING_PLAN_INFO, payload],
      (oldData) =>
        oldData
          ? {
              ...oldData,
              ...newData,
            }
          : oldData,
    );
  };
  return updateCache;
};

export const useBuyingPlanQueryData = <T = BuyingPlanInfo>(
  select: (data: BuyingPlanInfo) => T,
) => {
  const payload = useGetBuyingPlanPayload();

  const data = queryClient.getQueryData<BuyingPlanInfo>([
    QueryKey.BUYING_PLAN_INFO,
    payload,
  ]);

  return data ? select(data) : data;
};

// selectors
const selectRows = (data: BuyingPlanInfo) => data.items;
const selectTotals = (data: BuyingPlanInfo) => data.totals;
const selectIsLocked = (data: BuyingPlanInfo) => data.isLocked;
const selectIsRedDotShown = (data: BuyingPlanInfo) =>
  data.hasCampaignCollectionDraftOrders;
const selectCurrentVersionId = (data: BuyingPlanInfo) => data.currentVersionId;

export const useBuyingPlanRows = () => {
  return useBuyingPlanQueryData<BuyingPlanInfo['items']>(selectRows);
};
export const useBuyingPlanTotals = () => {
  return useBuyingPlanQueryData<BuyingPlanInfo['totals']>(selectTotals);
};
export const useIsSelectedPlanLocked = () => {
  return useBuyingPlanQueryData<BuyingPlanInfo['isLocked']>(selectIsLocked);
};
export const useIsRedDotShown = () => {
  return useBuyingPlanQueryData<
    BuyingPlanInfo['hasCampaignCollectionDraftOrders']
  >(selectIsRedDotShown);
};
export const useCurrentVersionId = () => {
  return useBuyingPlanQueryData<BuyingPlanInfo['currentVersionId']>(
    selectCurrentVersionId,
  );
};
